import { useState } from "react";
import { Button } from "@onengine/component-library";
import { getFieldType } from "@/components/features/FormBuilder/data/fieldTypes";
import type { AssistantPlan } from "@/api/form-builder/formAssistant.types";
import type { PlanStatus } from "@/store/form-builder/useAssistantStore";
import styles from "./Assistant.module.scss";

const STATUS_LABEL: Record<Exclude<PlanStatus, "pending">, string> = {
  approved: "Approved by you",
  cancelled: "Cancelled",
  replaced: "Replaced by a newer plan",
};

const SHOWN_OPTIONS = 5;

// A checklist can carry dozens of items, so long lists start short; the person
// can still open every option before approving.
function PlanOptions({ options }: { options: string[] }) {
  const [expanded, setExpanded] = useState(false);
  const hidden = options.length - SHOWN_OPTIONS;
  if (hidden <= 0) return <>: {options.join(", ")}</>;
  return (
    <>
      : {(expanded ? options : options.slice(0, SHOWN_OPTIONS)).join(", ")}{" "}
      <button
        type="button"
        className={styles.optionsToggle}
        aria-expanded={expanded}
        onClick={() => setExpanded(!expanded)}
      >
        {expanded ? "show fewer" : `and ${hidden} more`}
      </button>
    </>
  );
}

interface PlanCardProps {
  plan: AssistantPlan;
  status: PlanStatus;
  isBusy: boolean;
  onApprove: () => void;
  onChange: () => void;
  onCancel: () => void;
  /** What approving does, when it is more than building the plan in the preview. */
  approveHint?: string;
}

export function PlanCard({
  plan,
  status,
  isBusy,
  onApprove,
  onChange,
  onCancel,
  approveHint = "Nothing is built or shown in the preview until you approve.",
}: PlanCardProps) {
  const questionCount = plan.pages.reduce(
    (total, page) =>
      total + page.sections.reduce((sum, section) => sum + section.questions.length, 0),
    0,
  );

  return (
    <section className={styles.proposalCard} aria-label="Plan">
      <div className={styles.cardHeader}>
        <span className={styles.cardTitle}>{plan.title || "Plan"}</span>
        {status !== "pending" && <span className={styles.statusChip}>{STATUS_LABEL[status]}</span>}
      </div>
      <span className={styles.cardMeta}>
        {plan.pages.length} {plan.pages.length === 1 ? "page" : "pages"}, {questionCount}{" "}
        {questionCount === 1 ? "question" : "questions"}, {plan.logicRules.length}{" "}
        {plan.logicRules.length === 1 ? "logic rule" : "logic rules"}
      </span>
      <ol className={styles.planList}>
        {plan.pages.map((page, pageIndex) => (
          <li key={pageIndex}>
            <span className={styles.planPage}>{page.name}</span>
            {page.sections.map((section, sectionIndex) => (
              <div key={sectionIndex} className={styles.planSection}>
                <span className={styles.planSectionName}>{section.name}</span>
                <ul>
                  {section.questions.map((question, questionIndex) => (
                    <li key={questionIndex}>
                      {question.label}
                      {question.required && <span className={styles.required}> *</span>}
                      <span className={styles.planType}>
                        {" "}
                        {getFieldType(question.type)?.label ?? question.type}
                        {question.options && question.options.length > 0 && (
                          <PlanOptions options={question.options} />
                        )}
                      </span>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </li>
        ))}
      </ol>
      {plan.logicRules.length > 0 && (
        <ul className={styles.planRules}>
          {plan.logicRules.map((rule, index) => (
            <li key={index}>{rule}</li>
          ))}
        </ul>
      )}
      {plan.assumptions && plan.assumptions.length > 0 && (
        <ul className={styles.planAssumptions} aria-label="Assumptions">
          {plan.assumptions.map((assumption, index) => (
            <li key={index}>{assumption}</li>
          ))}
        </ul>
      )}
      {status === "pending" && (
        <>
          <div className={styles.cardActions}>
            <Button variant="primary" size="small" onClick={onApprove} isDisabled={isBusy}>
              Approve plan
            </Button>
            <Button variant="secondary" size="small" onClick={onChange} isDisabled={isBusy}>
              Change plan
            </Button>
            <Button variant="tertiary" size="small" onClick={onCancel} isDisabled={isBusy}>
              Cancel
            </Button>
          </div>
          <span className={styles.cardMeta}>{approveHint}</span>
        </>
      )}
    </section>
  );
}
